import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
} from '@mui/material';
import {
  Close as CloseIcon,
  Download as DownloadIcon,
  InsertDriveFile as FileIcon,
} from '@mui/icons-material';

interface FileViewerDialogProps {
  open: boolean;
  onClose: () => void;
  file: {
    name: string;
    url: string;
    mimeType?: string;
    size?: number;
  } | null;
}

export const FileViewerDialog: React.FC<FileViewerDialogProps> = ({
  open,
  onClose,
  file,
}) => {
  if (!file) return null;

  const isImage = file.mimeType?.startsWith('image/') ||
                  /\.(jpg|jpeg|png|gif|bmp|webp)$/i.test(file.name);
  const isPdf = file.mimeType === 'application/pdf' ||
                file.name.toLowerCase().endsWith('.pdf');

  const handleDownload = () => {
    const link = document.createElement('a');
    link.href = file.url;
    link.download = file.name;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const formatFileSize = (bytes?: number) => {
    if (!bytes) return '';
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return Math.round(bytes / Math.pow(1024, i) * 100) / 100 + ' ' + sizes[i];
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          minHeight: '60vh',
          maxHeight: '90vh',
        }
      }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
        <Box>
          <Typography variant="h6" component="div" noWrap>
            {file.name}
          </Typography>
          {file.size && (
            <Typography variant="caption" color="text.secondary">
              {formatFileSize(file.size)}
            </Typography>
          )}
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        {isImage ? (
          <Box
            sx={{
              width: '100%',
              height: '100%',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              p: 2,
            }}
          >
            <img
              src={file.url}
              alt={file.name}
              style={{
                maxWidth: '100%',
                maxHeight: '70vh',
                objectFit: 'contain',
                borderRadius: '8px',
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
              }}
            />
          </Box>
        ) : isPdf ? (
          <Box sx={{ width: '100%', height: '70vh', p: 2 }}>
            <iframe
              src={file.url}
              width="100%"
              height="100%"
              style={{ border: 'none', borderRadius: '8px' }}
              title={file.name}
            />
          </Box>
        ) : (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              height: '40vh',
              gap: 2,
            }}
          >
            <FileIcon sx={{ fontSize: 64, color: '#1976d2' }} />
            <Typography variant="h6" color="text.secondary">
              Preview not available
            </Typography>
            <Typography variant="body2" color="text.secondary" textAlign="center">
              This file type cannot be previewed in the browser.
              <br />
              Click download to save the file to your computer.
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          onClick={handleDownload}
          startIcon={<DownloadIcon />}
          variant="outlined"
        >
          Download
        </Button>
        <Button onClick={onClose} variant="contained">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};